import React, {Component} from 'react'
import Typography from '@material-ui/core/Typography'
import { DealSheetActions } from 'actionsets'
import StyleSheet from 'styles'
import Table, { TableBody, TableRow, TableCell } from 'components/CollapsibleTable'
import {IndexActions, MultiSelect, SortedTableHead} from 'components'
import { ImageAvatar, Pagination, ActionIndicator, DateTimePicker, SearchComboInput, FilterBar } from 'components'
import { connect } from 'react-redux'
import Dependent from 'containers/shared/Dependent'
import { CenteredContent } from 'containers'
import ShowIcon from '@material-ui/icons/KeyboardArrowRight'
import IconButton from '@material-ui/core/IconButton'
import Button from '@material-ui/core/Button'
import TextField from '@material-ui/core/TextField'
import { Checkbox } from 'components/styled/Checkbox'
import {humanize} from 'utils'
import { connectQueryString } from 'containers/shared'
import {NumberDisplay} from 'components/numbers'
import {FormattedMessage} from 'react-intl'

export class AdminDealsList extends Component {

  constructor(props) {
    super(props)
    DealSheetActions.bindActions(this)
    this.state = {
      anchorEl: null,
    }
  }

  dependsOn(){
    return this.load()
  }

  dependenciesMet(){
    return this.props.requests.length === 0
  }

  componentDidUpdate(prevProps) {
    if (prevProps && prevProps.query !== this.props.query) {
      this.load()
    }
  }

  load(){
    return this.actions.index(this.filter, { page: this.props.page}, this.props.order)
  }

  get filter() {
    const {filter, filter: {status}} = this.props
    return {...filter, status: (!status || Array.isArray(status)) ? status : [status] }
  }

  handleShowDeal = deal => () => {
    this.props.history.push(`deal_sheets/${deal.id}/summary`)
  }

  handleNewDealSheet = () => {
    this.actions.newRecord()
    this.props.history.push('/deal_sheets/new')
  }

  renderAvatar = (deal) => {
    const { issuerAccountName, emblemUrls } = deal
    return <ImageAvatar alt={ issuerAccountName } urls={ emblemUrls } className={styles.avatarImage}/>
  }

  renderCurrency = currency => amount => {
    return <NumberDisplay suffix={currency} blank="—">{amount}</NumberDisplay>
  }

  renderDeal = (deal) => {
    const { id, currency, name, ticker, status, saleCap, paymentExpected, actionNeeded } = deal
    const currencyCode = currency ? currency.code : ''
    const renderCurrency = this.renderCurrency(currencyCode)

    return (
      <TableRow key={ id }>
        <TableCell mobileHeading>
          <div className={ styles.avatar } onClick={() => this.props.history.push(`/deal_sheets/${deal.id}/`)}>
            { this.renderAvatar(deal) }
            <Typography variant='subtitle1' className={ styles.tagLine }>{name} {ticker && <span style={{fontWeight: 500}}>({ticker})</span>}</Typography>
          </div>
        </TableCell>
        <TableCell mobileLabel="Sales cap">{renderCurrency(saleCap)}</TableCell>
        <TableCell mobileLabel="Allocated">{renderCurrency(paymentExpected)}</TableCell>
        <TableCell mobileLabel="Status">{humanize(status)}</TableCell>
        <TableCell mobileHeading dontPrint className={styles.actionNeededCell}>
          <ActionIndicator on={actionNeeded} />
        </TableCell>
        <TableCell mobileHeading dontPrint className={styles.actionButtons}>
          <IconButton onClick={this.handleShowDeal(deal)}>
            <ShowIcon/>
          </IconButton>
        </TableCell>
      </TableRow>
    )
  }

  render = () => {
    const {dealSheets, totalPages} = this.props
    return (
      <CenteredContent>
        <Typography variant='h5'>
          <FormattedMessage id="deals.manage.title" />
        </Typography>
        <FilterBar>
          <SearchComboInput filter={this.filter}>
            <TextField member='name' label="Deal name" fullWidth/>
            <div>
              <Typography variant="subtitle1">Status</Typography>
              <MultiSelect member="status" options={everyStatus} defaultOptions={everyStatus} unsetWhenDefault/>
            </div>
            <div>
              <Typography variant="subtitle1">Start Date</Typography>
              <DateTimePicker member='startDate.start' label="From" showDate />
              <DateTimePicker member='startDate.end' label="To" showDate />
            </div>
            <div>
              <Typography variant="subtitle1">End Date</Typography>
              <DateTimePicker member='endDate.start' label="From" showDate />
              <DateTimePicker member='endDate.end' label="To" showDate />
            </div>
            <div>
              <Typography className={styles.checkBox}><Checkbox type="checkbox" member="actionNeeded" />Action Needed</Typography>
            </div>
          </SearchComboInput>
          <IndexActions exportLink="/api/deal_sheets.csv"/>
          <Button variant='contained' color='primary' onClick={this.handleNewDealSheet}>Create</Button>
        </FilterBar>
        <div className={styles.list}>
          <Table>
            <SortedTableHead>
              <TableRow>
                <TableCell orderBy={'name'}><FormattedMessage id="deals.table.heading.deal" /></TableCell>
                <TableCell orderBy={'salesCap'}>Sales cap</TableCell>
                <TableCell orderBy={'amountAllocated'}>Allocated</TableCell>
                <TableCell orderBy={'status'}>Status</TableCell>
                <TableCell dontPrint className={styles.actionNeededCell}>Action needed</TableCell>
                <TableCell dontPrint className={styles.actionButtons} />
              </TableRow>
            </SortedTableHead>
            <TableBody>
              {dealSheets.map(deal =>
                this.renderDeal(deal)
              )}
            </TableBody>
          </Table>
          <Pagination totalPages={totalPages} showAll/>
        </div>
      </CenteredContent>
    )
  }
}

const everyStatus = ['draft', 'pending_review', 'approved', 'published', 'active', 'closed', 'rejected']

const styles = StyleSheet({
  wrapper: {
  },
  list: {
    width: '100%',
    '& table.collapsed': {
      '& $avatarImage': {
        marginRight: 0
      },
    }
  },
  avatar: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  avatarImage: {
    marginRight: 20,
    '@media print': {
      width: 30,
      height: 30,
    }
  },
  tagLine: {
    paddingLeft: 8,
    fontSize: '16px',
    fontWeight: 700,
    color: '#464646',
    '@media(min-width: 800px)': {
      whiteSpace: 'nowrap',
    },
    '@media print': {
      fontSize: '12px',
    }
  },
  actionButtons: {
    width: 1,
    '& button': {
      width: '32px',
      height: '32px',
      padding: 0,
    }
  },
  actionNeededCell: {
    textAlign: 'center'
  },
  checkBox: {
    marginLeft: -13,
    marginBottom: 0,
  },
  pageActions: {
    flex: 1,
    textAlign: 'right',
    paddingRight: 15,
    margin: [-2, 0]
  }
})

export default connect(({dealSheets})=> dealSheets)(connectQueryString(Dependent({loader: true, wrapperClassName: styles.wrapper})(AdminDealsList)))